/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import {
	graphql,
	type PreloadedQuery,
	useFragment,
	usePaginationFragment,
	usePreloadedQuery,
} from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import { AnyAri } from '@atlassian/ari/any-ari';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import {
	type TProductDataRegions,
	type TProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import type { IssueLinkedPRs } from '@atlassian/search-client/hydration/pr-types';
import { type BackendExperiment, type ExperimentConfig } from '@atlassian/search-experiment/types';

import {
	is3pProductKeyForAnalytics,
	isProductKey,
	ProductKeys,
	ProductKeys3P,
	products1P,
} from '../../../common/constants/products';
import { ResultsStates } from '../../../common/constants/results';
import { type ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { IAnalyticsSearchPageResults, SearchResultSharedProps } from '../../../common/types';
import { type DebugResult } from '../../../common/ui/debug-overrides-panel';
import { DebugOverridesPanel } from '../../../common/ui/debug-overrides-panel/async';
import { getLinksFromText } from '../../../common/ui/link-attachments';
import {
	LINKED_RESULTS_DEFAULT_EXPAND_SIZE,
	LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND,
} from '../../../common/ui/linked-results';
import type { ResultShown } from '../../../common/utils/events/types';
import { triggerExposureForWeakLinkExp } from '../../../common/utils/experiments';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import {
	serveFederatedSlackExperiment,
	serveGitHubPRHydrationExperiment,
} from '../../../common/utils/experiments/experiment-utils';
import {
	getIntegrationKey,
	getProductFromProviderId,
} from '../../../common/utils/search-result-utils';
import { getTeamId } from '../../../common/utils/teams';
import { getResultConnectorSource } from '../../../common/utils/third-party-utils';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import useDwellTime from '../../../controllers/dwell';
import type { SelectedFiltersReturnType } from '../../../controllers/filters/selected/get-selected-filters';
import { useFeatures } from '../../../controllers/store/bootstrap';
import {
	useSearchActions,
	useWasContentClearedBySpaceChange,
} from '../../../controllers/store/search';
import { filter as spaceFilter } from '../../../controllers/store/search/filters/space';

import type { searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData.graphql';
import type { searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsBody_connectionData.graphql';
import type { searchResults_AtlassianSearchPage_SearchResultsSyncInner_queryData$key } from './__generated__/searchResults_AtlassianSearchPage_SearchResultsSyncInner_queryData.graphql';
import type { SearchResultsAsyncPaginationQuery } from './__generated__/SearchResultsAsyncPaginationQuery.graphql';
import type { searchResultsAsyncQuery } from './__generated__/searchResultsAsyncQuery.graphql';
import type { SearchResultsPaginationQuery } from './__generated__/SearchResultsPaginationQuery.graphql';
import type { searchResultsQuery } from './__generated__/searchResultsQuery.graphql';
import AutoCorrection from './auto-correction';
import { SearchResultsListLiteRenderer } from './search-result-list-lite-renderer';
import { isSearchCompassComponent } from './search-result/search-result-renderer/search-compass-component';
import {
	isSearchJiraIssue,
	type JiraIssueStatusAndColorName,
} from './search-result/search-result-renderer/search-jira-issue';
import { SearchResultsListDefaultRenderer } from './search-results-list-default-renderer';
import type { SearchResultsProps } from './types';
import { useRenderResultsOptimisation } from './use-render-results-optimisation';
import { useSearchResultsAnalytics } from './use-search-results-analytics';
import { useSearchResultsVitalStats } from './use-search-results-vital-stats';
import {
	fetchAndUpdateJiraFields,
	fetchAndUpdateJiraLinkedPRs,
	fetchAndUpdateTeamNames,
} from './utils';

const PAGINATION_UNUSED_PAGE_SIZE = 30;

/**
 * SearchResultsResolver props — a unified entry point that delegates to either
 * SearchResults (sync) or SearchResultsAsync based on feature gate.
 * main.tsx renders this component so it doesn't need to know about the FG.
 *
 * Only one of the two query types ever exists at a time (determined by the
 * search_async_ssr feature gate), so a single union-typed ref is sufficient.
 */
export type SearchResultsResolverProps = SearchResultSharedProps & {
	searchResultsQueryRef:
		| PreloadedQuery<searchResultsQuery>
		| PreloadedQuery<searchResultsAsyncQuery>;
	aggAbsoluteUrl?: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	experimentConfig: ExperimentConfig;
	productEdition: TProductEdition | undefined;
	productDataRegions: TProductDataRegions | undefined;
	isJiraIssueIndexed: boolean;
	setResultsState: (state: ResultsStates) => void;
	getSelectedFilters: () => SelectedFiltersReturnType;
	clearFilters: () => void | Promise<void>;
	logError: (error: any) => Promise<void>;
	clearReferrer: () => void | Promise<void>;
	referrer: string | undefined;
	autoCorrectPortalRef?: React.RefObject<HTMLDivElement>;
	smartAnswersPortalRef?: React.RefObject<HTMLDivElement>;
	debugPortalRef?: React.RefObject<HTMLDivElement>;
	setFederatedSlackCount?: (count: number | null) => void;
	setFederatedOutlookMailCount?: (count: number | null) => void;
	setFederatedGmailCount?: (count: number | null) => void;
	setProductsWithTimeout?: React.Dispatch<React.SetStateAction<ProductKeys[] | undefined>>;
	bodyRef: React.RefObject<HTMLDivElement> | undefined;
	maxResultsToShow: number | undefined;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	mainContentWidth: number | undefined;
	backendExperimentRef: React.RefObject<BackendExperiment>;
};

type SearchResultsSharedProps = Omit<SearchResultsProps, 'searchResultsQuery'>;

type SearchResultsAsyncProps = Omit<SearchResultsSharedProps, never> & {
	searchResultsAsyncQuery: PreloadedQuery<searchResultsAsyncQuery>;
};

/**
 * Unified entry point for search results rendering.
 * Delegates to SearchResultsAsync when search_async_ssr is enabled,
 * otherwise falls back to SearchResults (sync with pagination).
 *
 * main.tsx renders this component so it doesn't need FG-awareness.
 *
 * New three-tier construction to simplify each portion of the relay query, likely will be seperate files in future
 * - SearchResults (SearchResultsAsync) = toplevel reference to usePreloadedQuery() -> handles preloading, variable inputs and connects to query-plan
 * - SearchResultsSyncInner (SearchResultsAsyncInner) = usePaginationFragment (useFragment) -> defines the actual search() query, handles pagation etc
 * - SearchResultsBody = defines the actual fields for the query -> defines the body of the response
 * (temporary fourth tier SearchResultsResolver that handles search_async_ssr branching)
 *
 * This structure allows us to FG the query path code itself, and splits the query definition and requested payload into manageable chunks
 *
 * search_async_ssr=false
 *   SearchResultsResolver
 * 	 └── SearchResults (sync) - note: not renamed SearchResultsInner to avoid renaming the toplevel relay query!
 * 	 	└── SearchResultsSyncInner (sync)
 * 	 		└── SearchResultsBody (supports sync/async)
 * search_async_ssr=true
 *   SearchResultsResolver
 * 	 └── SearchResultsAsync (async)
 * 	 	└── SearchResultsAsyncInner (async)
 * 	 		└── SearchResultsBody (supports sync/async)
 *
 * After search_async_ssr cleanup:
 * 	 SearchResults - still not renamed! Doesn't change the name of our toplevel query, which is a hard requirement.
 * 	 └── SearchResultsInner
 * 	 	└── SearchResultsBody
 */
export const SearchResultsResolver = ({
	searchResultsQueryRef,
	...sharedProps
}: SearchResultsResolverProps): React.JSX.Element => {
	if (fg('search_async_ssr')) {
		return (
			<SearchResultsAsync
				searchResultsAsyncQuery={searchResultsQueryRef as PreloadedQuery<searchResultsAsyncQuery>}
				{...sharedProps}
			/>
		);
	}

	return (
		<SearchResults
			searchResultsQuery={searchResultsQueryRef as PreloadedQuery<searchResultsQuery>}
			{...sharedProps}
		/>
	);
};

type SearchResultsAsyncInnerProps = {
	queryData: searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key;
	queryReference: PreloadedQuery<searchResultsAsyncQuery>;
	sharedProps: SearchResultsSharedProps;
};

type SearchResultsSyncInnerProps = {
	queryData: searchResults_AtlassianSearchPage_SearchResultsSyncInner_queryData$key;
	queryReference: PreloadedQuery<searchResultsQuery>;
	sharedProps: SearchResultsSharedProps;
};

type SearchResultsBodyProps = {
	connectionData: searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key | null;
	isCachedResult?: boolean | null;
	isLoadingNext: boolean;
	hasPreviousRef: React.MutableRefObject<boolean>;
	cursor: string | null | undefined;
	fetchNextPage: (nextCursor: string) => void;
	queryReference: PreloadedQuery<searchResultsQuery> | PreloadedQuery<searchResultsAsyncQuery>;
	sharedProps: SearchResultsSharedProps;
};

export const SearchResults = ({
	searchResultsQuery: searchResultsQueryRef,
	...sharedProps
}: SearchResultsProps): React.JSX.Element => {
	const queryData = usePreloadedQuery<searchResultsQuery>(
		graphql`
			query searchResultsQuery(
				$query: String!
				$cloudIdARI: String!
				$tenantId: ID!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$after: String
				$disableQueryReplacement: Boolean
				$experience: String!
				$enableHighlighting: Boolean
				$fanoutExperimentCohort: String
				$includeIconUrl: Boolean = false
				$includePositions: Boolean = false
				$includeAssigneeField: Boolean = false
				$includeThirdPartyId: Boolean = false
				$includeNavBoostScore: Boolean = false
				$includePassionfruitAsk: Boolean = false
				$includeTotalCounts: Boolean = false
				$includeIssueTypeField: Boolean = false
				$includeJiraLiteModeFields: Boolean = false
			) {
				...searchResults_AtlassianSearchPage_SearchResultsSyncInner_queryData
					@arguments(
						includeAssigneeField: $includeAssigneeField
						includeIconUrl: $includeIconUrl
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeNavBoostScore: $includeNavBoostScore
						includePassionfruitAsk: $includePassionfruitAsk
						includeTotalCounts: $includeTotalCounts
						includeIssueTypeField: $includeIssueTypeField
						includeJiraLiteModeFields: $includeJiraLiteModeFields
					)
			}
		`,
		searchResultsQueryRef,
	);

	return (
		<SearchResultsSyncInner
			queryData={queryData}
			queryReference={searchResultsQueryRef}
			sharedProps={sharedProps}
		/>
	);
};

const SearchResultsSyncInner = ({
	queryData,
	queryReference,
	sharedProps,
}: SearchResultsSyncInnerProps): React.JSX.Element => {
	// Pagination hasPrevious state (hasPreviousPage from usePaginationFragment does not work with aggregator)
	// See https://hello.atlassian.net/wiki/x/lYYyQQE
	const hasPreviousRef = useRef(false);

	// Note that aggregator doesn't quite match the expected specification here
	// - hasNext is an available prop but will always be false.
	// - loadNext takes (limit: number) to set page size, but aggregator ignores it
	// See https://hello.atlassian.net/wiki/x/lYYyQQE
	const { data, loadNext, isLoadingNext } = usePaginationFragment<
		SearchResultsPaginationQuery,
		searchResults_AtlassianSearchPage_SearchResultsSyncInner_queryData$key
	>(
		graphql`
			fragment searchResults_AtlassianSearchPage_SearchResultsSyncInner_queryData on Query
			@refetchable(queryName: "SearchResultsPaginationQuery")
			@argumentDefinitions(
				includeAssigneeField: { type: "Boolean", defaultValue: false }
				includeIconUrl: { type: "Boolean", defaultValue: false }
				includeIssueTypeField: { type: "Boolean", defaultValue: false }
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includeTotalCounts: { type: "Boolean", defaultValue: false }
			) {
				search {
					search(
						experience: $experience
						enableHighlighting: $enableHighlighting
						query: $query
						first: $limit
						after: $after
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						fanoutExperimentCohort: $fanoutExperimentCohort
						filters: {
							entities: $entities
							locations: [$cloudIdARI]
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							assetsFilters: $assetsFilters
							thirdPartyFilters: $thirdPartyFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
					) @connection(key: "SearchResultsPagination__search") {
						...searchResults_AtlassianSearchPage_SearchResultsBody_connectionData
							@arguments(
								includeAssigneeField: $includeAssigneeField
								includeIconUrl: $includeIconUrl
								includeIssueTypeField: $includeIssueTypeField
								includeJiraLiteModeFields: $includeJiraLiteModeFields
								includePassionfruitAsk: $includePassionfruitAsk
								includePositions: $includePositions
								includeThirdPartyId: $includeThirdPartyId
								includeNavBoostScore: $includeNavBoostScore
								includeTotalCounts: $includeTotalCounts
							)
						edges {
							node {
								__typename
							}
						}
						pageInfo {
							endCursor
						}
					}
				}
			}
		`,
		queryData,
	);

	// Pagination
	const cursor = data.search?.search?.pageInfo?.endCursor;
	const fetchNextPage = useCallback(
		(nextCursor: string) => {
			// Double check we're fetching with the cursor from our current results
			if (nextCursor === cursor) {
				loadNext(PAGINATION_UNUSED_PAGE_SIZE);
				hasPreviousRef.current = true;
			}
		},
		[loadNext, cursor],
	);

	return (
		<SearchResultsBody
			connectionData={data.search?.search ?? null}
			isLoadingNext={isLoadingNext}
			hasPreviousRef={hasPreviousRef}
			cursor={cursor}
			fetchNextPage={fetchNextPage}
			queryReference={queryReference}
			sharedProps={sharedProps}
		/>
	);
};

const SearchResultsAsync = ({
	searchResultsAsyncQuery: searchResultsAsyncQueryRef,
	...sharedProps
}: SearchResultsAsyncProps): React.JSX.Element => {
	const queryData = usePreloadedQuery<searchResultsAsyncQuery>(
		graphql`
			query searchResultsAsyncQuery(
				$query: String!
				$cloudIdARI: String!
				$tenantId: ID!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$after: String
				$disableQueryReplacement: Boolean
				$experience: String!
				$enableHighlighting: Boolean
				$fanoutExperimentCohort: String
				$expectResult: Boolean
				$includeIconUrl: Boolean = false
				$includePositions: Boolean = false
				$includeAssigneeField: Boolean = false
				$includeThirdPartyId: Boolean = false
				$includeNavBoostScore: Boolean = false
				$includePassionfruitAsk: Boolean = false
				$includeTotalCounts: Boolean = false
				$includeIssueTypeField: Boolean = false
				$includeJiraLiteModeFields: Boolean = false
			) {
				...searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData
					@arguments(
						includeAssigneeField: $includeAssigneeField
						includeIconUrl: $includeIconUrl
						includePositions: $includePositions
						includeThirdPartyId: $includeThirdPartyId
						includeNavBoostScore: $includeNavBoostScore
						includePassionfruitAsk: $includePassionfruitAsk
						includeTotalCounts: $includeTotalCounts
						includeIssueTypeField: $includeIssueTypeField
						includeJiraLiteModeFields: $includeJiraLiteModeFields
					)
			}
		`,
		searchResultsAsyncQueryRef,
	);

	return (
		<SearchResultsAsyncInner
			queryData={queryData}
			queryReference={searchResultsAsyncQueryRef}
			sharedProps={sharedProps}
		/>
	);
};

const SearchResultsAsyncInner = ({
	queryData,
	queryReference,
	sharedProps,
}: SearchResultsAsyncInnerProps): React.JSX.Element => {
	const hasPreviousRef = useRef(false);

	const { data, loadNext, isLoadingNext } = usePaginationFragment<
		SearchResultsAsyncPaginationQuery,
		searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData$key
	>(
		graphql`
			fragment searchResults_AtlassianSearchPage_SearchResultsAsyncInner_queryData on Query
			@refetchable(queryName: "SearchResultsAsyncPaginationQuery")
			@argumentDefinitions(
				includeAssigneeField: { type: "Boolean", defaultValue: false }
				includeIconUrl: { type: "Boolean", defaultValue: false }
				includeIssueTypeField: { type: "Boolean", defaultValue: false }
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includePassionfruitAsk: { type: "Boolean", defaultValue: false }
				includePositions: { type: "Boolean", defaultValue: false }
				includeThirdPartyId: { type: "Boolean", defaultValue: false }
				includeNavBoostScore: { type: "Boolean", defaultValue: false }
				includeTotalCounts: { type: "Boolean", defaultValue: false }
			) {
				search {
					search: asyncSearch(
						experience: $experience
						enableHighlighting: $enableHighlighting
						query: $query
						first: $limit
						after: $after
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						fanoutExperimentCohort: $fanoutExperimentCohort
						expectResult: $expectResult
						filters: {
							entities: $entities
							locations: [$cloudIdARI]
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							assetsFilters: $assetsFilters
							thirdPartyFilters: $thirdPartyFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
					) @connection(key: "SearchResultsAsyncPagination__search") {
						isCachedResult
						...searchResults_AtlassianSearchPage_SearchResultsBody_connectionData
							@arguments(
								includeAssigneeField: $includeAssigneeField
								includeIconUrl: $includeIconUrl
								includeIssueTypeField: $includeIssueTypeField
								includeJiraLiteModeFields: $includeJiraLiteModeFields
								includePassionfruitAsk: $includePassionfruitAsk
								includePositions: $includePositions
								includeThirdPartyId: $includeThirdPartyId
								includeNavBoostScore: $includeNavBoostScore
								includeTotalCounts: $includeTotalCounts
							)
						edges {
							node {
								__typename
							}
						}
						pageInfo {
							endCursor
						}
					}
				}
			}
		`,
		queryData,
	);

	const cursor = data.search?.search?.pageInfo?.endCursor;
	const fetchNextPage = useCallback(
		(nextCursor: string) => {
			if (nextCursor === cursor) {
				loadNext(PAGINATION_UNUSED_PAGE_SIZE);
				hasPreviousRef.current = true;
			}
		},
		[loadNext, cursor],
	);

	return (
		<SearchResultsBody
			connectionData={data.search?.search ?? null}
			isCachedResult={data.search?.search?.isCachedResult}
			isLoadingNext={isLoadingNext}
			hasPreviousRef={hasPreviousRef}
			cursor={cursor}
			fetchNextPage={fetchNextPage}
			queryReference={queryReference}
			sharedProps={sharedProps}
		/>
	);
};

const SearchResultsBody = ({
	connectionData,
	isCachedResult,
	isLoadingNext,
	hasPreviousRef,
	cursor,
	fetchNextPage,
	queryReference,
	sharedProps,
}: SearchResultsBodyProps): React.JSX.Element => {
	const {
		aggAbsoluteUrl,
		cloudId,
		thirdPartyConfigs,
		selectedProduct,
		primaryProduct,
		appMode,
		searchSessionId,
		searchQuery,
		availableProducts,
		experimentConfig,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		setResultsState,
		getSelectedFilters,
		clearFilters,
		logError,
		clearReferrer,
		referrer,
		autoCorrectPortalRef,
		smartAnswersPortalRef,
		debugPortalRef,
		setFederatedSlackCount,
		setFederatedOutlookMailCount,
		setFederatedGmailCount,
		setProductsWithTimeout,
		bodyRef,
		maxResultsToShow,
		setMaxResultsToShow,
		mainContentWidth,
		backendExperimentRef,
	} = sharedProps;

	// Fragment for the search result body content within the connection
	const bodyData =
		useFragment<searchResults_AtlassianSearchPage_SearchResultsBody_connectionData$key>(
			graphql`
				fragment searchResults_AtlassianSearchPage_SearchResultsBody_connectionData on SearchItemConnection
				@argumentDefinitions(
					includeAssigneeField: { type: "Boolean", defaultValue: false }
					includeIconUrl: { type: "Boolean", defaultValue: false }
					includeAssets: { type: "Boolean!", provider: "./is-assets-enabled.relayprovider" }
					includeIssueTypeField: { type: "Boolean", defaultValue: false }
					includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
					includePassionfruitAsk: { type: "Boolean", defaultValue: false }
					includePositions: { type: "Boolean", defaultValue: false }
					includeThirdPartyId: { type: "Boolean", defaultValue: false }
					includeNavBoostScore: { type: "Boolean", defaultValue: false }
					includeTotalCounts: { type: "Boolean", defaultValue: false }
				) {
					edges {
						node {
							__typename
							id
							description
							linkedResults {
								id
								category
								connectorType
							}
							...searchResult_AtlassianSearchPage
								@arguments(
									includeAssigneeField: $includeAssigneeField
									includeIconUrl: $includeIconUrl
									includeAssets: $includeAssets
									includeIssueTypeField: $includeIssueTypeField
									includeJiraLiteModeFields: $includeJiraLiteModeFields
									includePassionfruitAsk: $includePassionfruitAsk
									includePositions: $includePositions
									includeThirdPartyId: $includeThirdPartyId
									includeNavBoostScore: $includeNavBoostScore
								)
							... on SearchResultGraphDocument {
								# required by analytics
								providerId
								integrationId
								connectorType
								connectorId
								connectorProduct
							}
							... on SearchResultCompassComponent {
								# we need this in this file so we can pass down the lazy-loaded team name
								ownerId
							}
							... on SearchResultJiraIssue {
								# we need this in this file so we can pass down the lazy-loaded issue status
								url
								issue {
									webUrl
								}
							}
							... on SearchConfluencePageBlogAttachment {
								# required by analytics
								socialSignal(tenantId: $tenantId) {
									signal
								}
							}
						}
					}
					queryInfo {
						originalQuery
						...autoCorrection_AtlassianSearchPage
					}
					traceId
					errors {
						statusCode
						product
						integration
					}
					totalCounts @include(if: $includeTotalCounts) {
						product
						count
					}
				}
			`,
			connectionData,
		);

	// It is possible that aggregator returns the same node twice, but then <SearchResult key={edge.node.id} /> breaks
	// So we need to dedup by id
	const edges = useMemo(() => {
		const seenIds = new Set();
		const edges = bodyData?.edges || [];
		return edges.filter((edge) => {
			const id = edge?.node?.id;
			if (seenIds.has(id)) {
				return false;
			} else {
				seenIds.add(id);
				return true;
			}
		});
	}, [bodyData?.edges]);

	const edgesToDisplay = useRenderResultsOptimisation({
		bodyRef,
		edges,
		maxResultsToShow,
		setMaxResultsToShow,
		queryReference,
	});

	const [features] = useFeatures();
	const isLiteMode = features?.lite?.enabled ?? false;

	const productsWithTimeoutsFromErrors: ProductKeys[] | undefined = useMemo(() => {
		const errors = bodyData?.errors || [];

		return errors
			.filter((e: { statusCode: number }) => e.statusCode === 408)
			.map((e) => e.integration ?? e.product)
			.flatMap((s) => (s && isProductKey(s) ? [s] : [])); // Flatmap for better type inference
	}, [bodyData?.errors]);

	useEffect(() => {
		// We only want to set products with timeout in all product search
		if (selectedProduct !== undefined) {
			return;
		}

		setProductsWithTimeout?.(productsWithTimeoutsFromErrors);
	}, [productsWithTimeoutsFromErrors, selectedProduct, setProductsWithTimeout]);

	const resultsForAnalytics = useMemo(() => transformResultsForAnalytics(edges), [edges]);
	const resultsLength = edges.length;

	// Set federated Slack count when selectedProduct is Slack
	useEffect(() => {
		const isSlackFederatedSearchEnabled = serveFederatedSlackExperiment(
			selectedProduct,
			undefined,
			{ fireExperimentExposure: false },
			thirdPartyConfigs,
		);
		if (
			selectedProduct === ProductKeys.Slack &&
			setFederatedSlackCount &&
			isSlackFederatedSearchEnabled
		) {
			setFederatedSlackCount(resultsLength);
		}
	}, [selectedProduct, resultsLength, setFederatedSlackCount, thirdPartyConfigs]);

	// Set federated Outlook Mail count when selectedProduct is Outlook Mail
	useEffect(() => {
		if (selectedProduct === ProductKeys3P.OutlookMail && setFederatedOutlookMailCount) {
			setFederatedOutlookMailCount(resultsLength);
		}
	}, [selectedProduct, resultsLength, setFederatedOutlookMailCount]);

	// Set federated Gmail count when selectedProduct is Gmail
	useEffect(() => {
		if (
			selectedProduct === ProductKeys3P.Gmail &&
			setFederatedGmailCount &&
			fg('formula_3p_gmail_result_count')
		) {
			const totalCounts = bodyData?.totalCounts;
			if (totalCounts) {
				const gmailCount = totalCounts.find(
					(tc: { product: string; count: number }) => tc.product === 'gmail',
				)?.count;
				if (gmailCount !== undefined) {
					setFederatedGmailCount(gmailCount);
				}
			}
		}
	}, [selectedProduct, bodyData?.totalCounts, setFederatedGmailCount]);

	const is3pGroupingEnabled = fg('fps_3p_grouping_rollout_gate');

	// Records dwell time for search results
	// TODO: refactor this to use props instead of RSS
	useDwellTime();

	// Compass team names
	const [teamNames, setTeamNames] = useState<Record<string, string | null>>({});

	const { teamIds, nodeIdMap } = useMemo(
		() => getCompassTeamIds(edges, selectedProduct),
		[edges, selectedProduct],
	);

	useEffect(() => {
		fetchAndUpdateTeamNames(cloudId, teamIds, nodeIdMap, setTeamNames, logError);
	}, [cloudId, teamIds, nodeIdMap, logError]);

	// Jira issue statuses
	const [issueStatuses, setIssueStatuses] = useState<
		Record<string, JiraIssueStatusAndColorName | null>
	>({});

	const issueKeys = useMemo(() => getJiraIssueKeys(edges), [edges]);

	useEffect(() => {
		fetchAndUpdateJiraFields(
			cloudId,
			issueKeys,
			aggAbsoluteUrl,
			setIssueStatuses,
			logError,
			undefined,
			primaryProduct,
		);
	}, [cloudId, issueKeys, aggAbsoluteUrl, setIssueStatuses, logError, primaryProduct]);

	// Jira issue linked PRs (GitHub PR hydration)
	// Only enabled when the user has GitHub connected and is enrolled in the
	// rovo_fps_jira_github_frontend_hydration experiment.
	const isJiraLinkedPRsEnabled = serveGitHubPRHydrationExperiment(
		thirdPartyConfigs,
		availableProducts,
	);
	const [linkedPRs, setLinkedPRs] = useState<IssueLinkedPRs>({});
	const { fireAnalyticsEvent: fireLinkedPRsAnalyticsEvent } = useAnalytics();

	const allIssueAris = useMemo(
		() => (isJiraLinkedPRsEnabled ? getJiraIssueARIs(edges) : []),
		[edges, isJiraLinkedPRsEnabled],
	);

	// Track which ARIs we've already requested to avoid duplicate fetches on pagination.
	// When edges grow (e.g., user loads more results), we only fetch PRs for newly added issues.
	// Uses a ref rather than state to avoid triggering re-renders or effect cycles.
	const fetchedArisRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!isJiraLinkedPRsEnabled) {
			return;
		}
		const newAris = allIssueAris.filter((ari) => !fetchedArisRef.current.has(ari));
		if (newAris.length === 0) {
			return;
		}
		for (const ari of newAris) {
			fetchedArisRef.current.add(ari);
		}
		fetchAndUpdateJiraLinkedPRs(
			newAris,
			cloudId,
			aggAbsoluteUrl,
			setLinkedPRs,
			logError,
			fireLinkedPRsAnalyticsEvent,
		);
	}, [
		allIssueAris,
		cloudId,
		aggAbsoluteUrl,
		logError,
		isJiraLinkedPRsEnabled,
		fireLinkedPRsAnalyticsEvent,
	]);

	const are3pResultsShown = useMemo(
		() => are3pItemsInResults(edges, thirdPartyConfigs),
		[edges, thirdPartyConfigs],
	);
	const resultsShown = useMemo(
		() => getResultsShown(edges, thirdPartyConfigs),
		[edges, thirdPartyConfigs],
	);

	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const wasContentClearedBySpaceChange = useWasContentClearedBySpaceChange();
	const searchActions = useSearchActions();
	const clearSpaceFilter = useCallback(async () => {
		await searchActions.clearSelectFilterOptions({ filterId: spaceFilter.id });
	}, [searchActions]);


	const intl = useIntl();
	const intentDetectionTriggered = useMemo(
		() =>
			fg('intent_detection_triggered_metrics_enabled')
				? isIntentDetectionTriggeredForExperiment(primaryProduct, intl.locale)
				: undefined,
		[primaryProduct, intl.locale],
	);

	useSearchResultsAnalytics({
		are3pResultsShown,
		backendExperimentRef,
		clearReferrer,
		cloudId,
		commonAttributes,
		edges,
		experimentConfig,
		getSelectedFilters,
		intentDetectionTriggered,
		isJiraIssueIndexed,
		isLoadingNext,
		isPaginatedQuery: hasPreviousRef.current,
		nonPrivacySafeAttributes,
		productDataRegions,
		productEdition,
		productsWithTimeoutsFromErrors,
		referrer,
		resultsForAnalytics,
		resultsShown,
		searchQuery,
	});

	useSearchResultsVitalStats({
		edges,
		searchSessionId,
		isCQL: false,
		isCachedResult: isCachedResult ?? undefined,
	});

	useEffect(() => {
		if (isLoadingNext) {
			return;
		}

		if (
			thirdPartyConfigs?.[ProductKeys3P.GoogleDrive]?.impersonationEnabled &&
			selectedProduct === undefined
		) {
			triggerExposureForWeakLinkExp();
		}
	}, [isLoadingNext, selectedProduct, thirdPartyConfigs]);

	// Update if there are no results
	useEffect(() => {
		setResultsState(edges.length === 0 ? ResultsStates.NO_RESULTS : ResultsStates.RESULTS);
	}, [edges, setResultsState]);

	const queryInfo = bodyData?.queryInfo;
	const containsSearchResults = edges.length > 0;

	const debugResults: DebugResult[] = debugPortalRef?.current
		? edges.map((edge) => edge?.node ?? {})
		: [];
	const traceId = bodyData?.traceId ?? undefined;

	return (
		<>
			<UFOCustomCohortData dataKey="wordCount" value={commonAttributes.wordCount} />
			{fg('search_async_ssr') && (
				<UFOCustomCohortData dataKey="asyncQueryCachedResult" value={!!isCachedResult} />
			)}
			{queryInfo &&
				autoCorrectPortalRef &&
				autoCorrectPortalRef.current &&
				createPortal(
					<AutoCorrection node={queryInfo} appMode={appMode} cloudId={cloudId} />,
					autoCorrectPortalRef.current,
					'search-results-auto-correction',
				)}
			{debugPortalRef?.current &&
				createPortal(
					<DebugOverridesPanel
						traceId={traceId}
						backendExperiment={backendExperimentRef.current ?? undefined}
						results={debugResults}
					/>,
					debugPortalRef.current,
				)}
			{
				// the new Rovo Home experience renders Jira issues and projects in a table view in FPS
				isLiteMode ? 
				<SearchResultsListLiteRenderer
					containsSearchResults={containsSearchResults}
					edgesToDisplay={edgesToDisplay}
					cloudId={cloudId}
					appMode={appMode}
					searchQuery={searchQuery}
					thirdPartyConfigs={thirdPartyConfigs}
					selectedProduct={selectedProduct}
					primaryProduct={primaryProduct}
					searchSessionId={searchSessionId}
					originalQuery={queryInfo?.originalQuery}
					availableProducts={availableProducts}
					logError={logError}
					teamNames={teamNames}
					issueStatuses={issueStatuses}
					linkedPRs={linkedPRs}
					is3pGroupingEnabled={is3pGroupingEnabled}
					smartAnswersPortalRef={smartAnswersPortalRef}
					mainContentWidth={mainContentWidth}
					wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
					clearSpaceFilter={clearSpaceFilter}
					clearFilters={clearFilters}
					getSelectedFilters={getSelectedFilters}
					cursor={cursor}
					isLoadingNext={isLoadingNext}
					fetchNextPage={fetchNextPage}
				/>
				:
				<SearchResultsListDefaultRenderer
					containsSearchResults={containsSearchResults}
					edgesToDisplay={edgesToDisplay}
					cloudId={cloudId}
					appMode={appMode}
					searchQuery={searchQuery}
					thirdPartyConfigs={thirdPartyConfigs}
					selectedProduct={selectedProduct}
					primaryProduct={primaryProduct}
					searchSessionId={searchSessionId}
					originalQuery={queryInfo?.originalQuery}
					availableProducts={availableProducts}
					logError={logError}
					teamNames={teamNames}
					issueStatuses={issueStatuses}
					linkedPRs={linkedPRs}
					is3pGroupingEnabled={is3pGroupingEnabled}
					smartAnswersPortalRef={smartAnswersPortalRef}
					mainContentWidth={mainContentWidth}
					wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
					clearSpaceFilter={clearSpaceFilter}
					clearFilters={clearFilters}
					getSelectedFilters={getSelectedFilters}
					cursor={cursor}
					isLoadingNext={isLoadingNext}
					fetchNextPage={fetchNextPage}
				/>
			}
		</>
	);
};

export const are3pItemsInResults: (
	edges: readonly Minimal3pItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => boolean = (
	edges: readonly Minimal3pItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	return !!edges?.some(({ node }) => {
		const productKey =
			getProductFromProviderId(thirdPartyConfigs, node?.providerId) ||
			getIntegrationKey(node?.integrationId);
		if (!productKey) {
			// if no integration id it's a 1p
			return false;
		}
		return is3pProductKeyForAnalytics(productKey);
	});
};

type MinimalCompassComponentEdge = {
	node?: {
		id: string;
		__typename?: string;
		ownerId?: string | null;
	} | null;
};

function getCompassTeamIds(
	edges: readonly MinimalCompassComponentEdge[],
	selectedProduct?: ProductKeys,
): { teamIds: string[]; nodeIdMap: Record<string, string | null> } {
	if (selectedProduct !== ProductKeys.Compass && selectedProduct !== undefined) {
		return { teamIds: [], nodeIdMap: {} };
	}
	const nodeIdMap: Record<string, string | null> = {};
	const ids = new Set<string>();
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i]?.node;
		let ownerId = node?.ownerId;
		if (isSearchCompassComponent(node)) {
			const teamId = getTeamId(ownerId);
			if (teamId) {
				nodeIdMap[node.id] = teamId;
				ids.add(teamId);
			} else {
				nodeIdMap[node.id] = null;
			}
		}
	}
	return { teamIds: Array.from(ids), nodeIdMap };
}

type MinimalJiraIssueComponentEdge = {
	node?: {
		id: string;
		__typename?: string;
		url?: string | null;
		issue?: {
			webUrl?: string | null;
		} | null;
	} | null;
};

function getJiraIssueARIs(edges: readonly MinimalJiraIssueComponentEdge[]): string[] {
	const issueArisSet = new Set<string>();
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i]?.node;
		if (node?.id && isSearchJiraIssue(node)) {
			issueArisSet.add(node.id);
		}
	}
	return Array.from(issueArisSet);
}

function getJiraIssueKeys(edges: readonly MinimalJiraIssueComponentEdge[]): string[] {
	const issueKeysSet = new Set<string>();
	for (let i = 0; i < edges.length; i++) {
		const node = edges[i]?.node;
		if (node?.id && isSearchJiraIssue(node)) {
			const issueKey = node.issue?.webUrl?.split('/').slice(-1)[0] || node?.url?.split('/').slice(-1)[0];
			if (issueKey) {
				issueKeysSet.add(issueKey);
			}
		}
	}
	return Array.from(issueKeysSet);
}

type Minimal3pItemsEdge = {
	node?: {
		__typename?: string;
		id: string;
		providerId?: string | null;
		integrationId?: string | null;
	} | null;
};

type MinimalLinkedResultItemsEdge = {
	node?: {
		__typename?: string;
		id: string;
		providerId?: string | null;
		integrationId?: string | null;
		description: string | null;
		linkedResults?: readonly { id: string; category: string }[] | null;
		connectorType?: string | null;
		connectorId?: string | null;
		connectorProduct?: string | null;
	} | null;
};

export const getResultsShown: (
	edges: readonly MinimalLinkedResultItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => ResultShown[] = (
	edges: readonly MinimalLinkedResultItemsEdge[] | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): ResultShown[] => {
	return (
		edges?.map(({ node }, index) => {
			const productKey =
				getProductFromProviderId(thirdPartyConfigs, node?.providerId) ||
				getIntegrationKey(node?.integrationId);

			const [firstPartyProduct] = getProductAndIDFromARI(node?.id || '');

			const resultAri = AnyAri.parse(node?.id || '');
			let hasLinkInDescription = false;
			if (resultAri.resourceOwner in products1P) {
				const links = getLinksFromText(node?.description ?? '');
				if (links && links.length > 0) {
					hasLinkInDescription = true;
				}
			}

			const shouldShowBoostedLinks = node?.linkedResults
				? node?.linkedResults.length > 0 && index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
				: undefined;

			const connectorSourceType = getResultConnectorSource({
				connectorType: node?.connectorType,
				resultNodeId: node?.id || '',
				productKey: productKey,
				thirdPartyConfigs,
			});

			// Build connectorInfo for unified 3P instrumentation
			// Only include for 3P results (where productKey exists)
			const connectorInfo = productKey
				? {
						provider_id: node?.providerId || undefined,
						connector_id: node?.connectorId || undefined,
						connector_position: index,
						connector_product: node?.connectorProduct || undefined,
						connector_type: node?.connectorType || undefined,
						ari: node?.id,
						metadata: {
							// Add connectorWorkspaceId when available from backend
							// For now, only include fanoutResultsShown if present
							...(node?.linkedResults &&
							node?.linkedResults.length > 0 &&
							index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
								? {
										fanOutResultsShown: node.linkedResults
											.slice(0, LINKED_RESULTS_DEFAULT_EXPAND_SIZE)
											.map((link) => {
												let is3P = false;
												try {
													is3P = !(AnyAri.parse(link.id).resourceOwner in products1P);
												} catch {
													is3P = false;
												}
												return {
													id: link.id,
													category: link.category,
													is3P,
												};
											}),
									}
								: {}),
						},
					}
				: undefined;

			return {
				id: node?.id,
				position: index,
				product: productKey || firstPartyProduct,
				metadata: {
					connectorType: connectorSourceType,
					boostedLinksShown: shouldShowBoostedLinks,
					fanoutResultsShown:
						node?.linkedResults &&
						node?.linkedResults.length > 0 &&
						index <= LINKED_RESULTS_TOP_K_INDEX_DEFAULT_EXPAND
							? node.linkedResults.slice(0, LINKED_RESULTS_DEFAULT_EXPAND_SIZE).map((link) => {
									let is3P = false;
									try {
										is3P = !(AnyAri.parse(link.id).resourceOwner in products1P);
									} catch {
										is3P = false;
									}
									return {
										id: link.id,
										category: link.category,
										is3P,
									};
								})
							: undefined,
					hasLinkInDescription,
				},
				connectorInfo,
			} as ResultShown;
		}) || []
	);
};

type MinimalAnalyticsEdge = {
	node?: {
		id: string;
		__typename?: string;
		integrationId?: string | null;
		providerId?: string | null;
		socialSignal?: any | null;
		connectorType?: string | null;
		connectorId?: string | null;
		connectorProduct?: string | null;
	} | null;
};

/**
 * Relay and RSS results have different types
 * Shared code (like result analytics) has typing assuming RSS
 * We solve this by building interfaces that accept both, such as IAnalyticsSearchPageResults
 *
 * We likely need a better approach for coalescing RSS and Relay result node typing, for shared code as we migrate away from RSS
 */
const transformResultsForAnalytics = (
	edges: MinimalAnalyticsEdge[],
): IAnalyticsSearchPageResults => {
	const nodes =
		edges
			?.filter((edge) => !!edge?.node) // drop undefined nodes
			?.map((edge) => {
				const node = edge.node;
				return {
					id: node?.id || '',
					__typename: node?.__typename || '',
					integrationId: node?.integrationId || '',
					providerId: node?.providerId || '',
					socialSignal: node?.socialSignal?.signal ? node?.socialSignal : null,
					connectorType: node?.connectorType || null,
				};
			}) || [];

	return {
		edges: nodes.map((node) => {
			return { node };
		}),
	};
};
